import {
  ArrowRight, Check, CreditCard, Headphones, Heart, Info, Layers,
  MessageCircle, MessageSquare, Moon, Package, Phone, PhoneCall, Play,
  RefreshCw, Shield, Sparkles, Star, Store, Sun, Target, TrendingUp,
  Truck, Users, X, Zap, AlertTriangle, ChevronDown,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type IconName =
  | 'arrow-right' | 'check' | 'credit-card' | 'headphones' | 'heart' | 'info'
  | 'layers' | 'message-circle' | 'message-square' | 'moon' | 'package'
  | 'phone' | 'phone-call' | 'play' | 'refresh-cw' | 'shield' | 'sparkles'
  | 'star' | 'store' | 'sun' | 'target' | 'trending-up' | 'truck' | 'users'
  | 'x' | 'zap' | 'alert-triangle' | 'chevron-down';

export const ICONS: Record<IconName, LucideIcon> = {
  'arrow-right': ArrowRight, 'check': Check, 'credit-card': CreditCard,
  'headphones': Headphones, 'heart': Heart, 'info': Info, 'layers': Layers,
  'message-circle': MessageCircle, 'message-square': MessageSquare, 'moon': Moon,
  'package': Package, 'phone': Phone, 'phone-call': PhoneCall, 'play': Play,
  'refresh-cw': RefreshCw, 'shield': Shield, 'sparkles': Sparkles, 'star': Star,
  'store': Store, 'sun': Sun, 'target': Target, 'trending-up': TrendingUp,
  'truck': Truck, 'users': Users, 'x': X, 'zap': Zap,
  'alert-triangle': AlertTriangle, 'chevron-down': ChevronDown,
};

export type UseCaseChart = 'compare' | 'donut' | 'line' | 'bars';

export interface UseCase {
  icon: IconName;
  tintBg: string;
  tintFg: string;
  title: string;
  desc: string;
  tag: string;
  metric: string;
  metricLabel: string;
  chart: UseCaseChart;
  caption: string;
  before?: number;
  after?: number;
  unit?: string;
  value?: number;
  series?: number[];
  labels?: string[];
}

export const USE_CASES: UseCase[] = [
  {
    icon: 'refresh-cw', tintBg: 'var(--orange-50)', tintFg: 'var(--orange-600)',
    title: 'Abandoned cart recovery',
    desc: 'AI calls the customer minutes after they bail, answers doubts, applies an offer, sends a payment link.',
    tag: 'Direct revenue recovery', metric: '+23%', metricLabel: 'carts recovered',
    chart: 'compare', before: 2, after: 23, unit: '%',
    caption: 'Recovery rate vs. email-only follow-ups',
  },
  {
    icon: 'shield', tintBg: 'var(--info-bg)', tintFg: 'var(--info)',
    title: 'COD & order confirmation',
    desc: 'Confirm cash-on-delivery orders before they ship. Cut RTO and fake orders across IN, MENA & SEA.',
    tag: 'Lower RTO losses', metric: '-34%', metricLabel: 'return-to-origin',
    chart: 'compare', before: 28, after: 9, unit: '%',
    caption: 'RTO rate before and after confirmation calls',
  },
  {
    icon: 'truck', tintBg: 'var(--warning-bg)', tintFg: 'var(--warning)',
    title: 'Failed delivery recovery',
    desc: 'When a delivery fails, the AI calls to reschedule or fix the address before the parcel returns.',
    tag: 'Saves logistics cost', metric: '71%', metricLabel: 'deliveries saved',
    chart: 'donut', value: 71,
    caption: 'Failed deliveries rescued on the first call',
  },
  {
    icon: 'message-square', tintBg: 'var(--navy-50)', tintFg: 'var(--navy-700)',
    title: 'Return reason calls',
    desc: 'Understand why customers return, offer exchanges or store credit, and reduce refund leakage.',
    tag: 'Less refund leakage', metric: '+42%', metricLabel: 'kept as exchange',
    chart: 'donut', value: 42,
    caption: 'Returns converted to exchange or store credit',
  },
  {
    icon: 'headphones', tintBg: 'var(--info-bg)', tintFg: 'var(--info)',
    title: 'Order status (inbound)',
    desc: 'Customers call your number and the AI answers "where is my order?" instantly, day or night.',
    tag: 'Cuts support load', metric: '1.2s', metricLabel: 'avg. answer time',
    chart: 'bars', series: [320, 240, 150, 90], labels: ['Q1', 'Q2', 'Q3', 'Q4'],
    caption: 'Weekly support tickets after going live',
  },
  {
    icon: 'sparkles', tintBg: 'var(--orange-50)', tintFg: 'var(--orange-600)',
    title: 'Product inquiry calls',
    desc: 'Help confused buyers with size, fit and compatibility questions so they buy with confidence.',
    tag: 'Higher conversion', metric: '+18%', metricLabel: 'inquiry → purchase',
    chart: 'compare', before: 31, after: 49, unit: '%',
    caption: 'Conversion when a question gets answered live',
  },
  {
    icon: 'heart', tintBg: 'var(--navy-50)', tintFg: 'var(--navy-700)',
    title: 'Repeat & winback calls',
    desc: 'Reach past customers at the right moment with replenishment nudges and personalised offers.',
    tag: 'Extra repeat sales', metric: '3.4x', metricLabel: 'repeat order rate',
    chart: 'line', series: [10, 14, 19, 23, 30, 34],
    caption: 'Repeat-purchase index over six months',
  },
  {
    icon: 'star', tintBg: 'var(--warning-bg)', tintFg: 'var(--warning)',
    title: 'Review & testimonial calls',
    desc: 'Call happy customers after delivery to collect reviews and build social proof automatically.',
    tag: 'Builds social proof', metric: '5.2x', metricLabel: 'more reviews',
    chart: 'bars', series: [12, 28, 44, 63], labels: ['M1', 'M2', 'M3', 'M4'],
    caption: 'Reviews collected per month after delivery calls',
  },
];

export const FAQS = [
  { q: 'Do I need to write any code?',
    a: 'No. Paste your Shopify store URL, turn on the scenarios you want, and CallXcel handles the rest. Most stores are live in under 20 minutes.' },
  { q: 'What does the AI voice sound like?',
    a: 'Natural and human. You can choose from several personas and preview each voice. The AI discloses it is an assistant where required and follows your brand tone.' },
  { q: 'Which languages are supported?',
    a: 'English (IN/US/UK), Hindi, and a growing set of regional languages. You can set a default language globally and override it per scenario.' },
  { q: 'How does billing work?',
    a: 'Start free with 50 AI calls. After that, plans combine a monthly subscription with per-minute usage so you only pay for conversations that happen.' },
  { q: 'Is it compliant with calling regulations?',
    a: 'Yes. CallXcel honors do-not-call lists, supports opt-out keywords, sets quiet hours, and can disclose the AI agent automatically based on your region.' },
  { q: 'Can it integrate with my shipping and helpdesk tools?',
    a: 'CallXcel connects with Shopify, shipping aggregators like Shiprocket and Delhivery, subscription apps, and can hand off to a human or push to your CRM.' },
];

export const AUDIENCES: { icon: IconName; title: string; desc: string }[] = [
  { icon: 'trending-up', title: 'Stores doing $30k+/month', desc: 'Enough order volume to see recovered revenue fast.' },
  { icon: 'package',     title: 'High-AOV brands',           desc: 'Furniture, electronics, jewelry, wellness, supplements, fashion.' },
  { icon: 'truck',       title: 'COD-heavy ecommerce',       desc: 'India, MENA and Southeast Asia stores fighting RTO.' },
  { icon: 'refresh-cw',  title: 'Stores with many abandoned carts', desc: 'Turn drop-offs into conversations, not just emails.' },
  { icon: 'users',       title: 'Brands with small support teams',  desc: 'Let AI handle inbound order-status and FAQ calls 24/7.' },
  { icon: 'store',       title: 'Shopify agencies',          desc: 'Manage 20-100 stores with multi-store dashboards and white-label reports.' },
];

export const TESTIMONIALS = [
  { quote: 'We recovered carts that email never touched. CallXcel paid for itself in the first week, and the voice agent actually sounds human.',
    name: 'Rohan Mehta', role: 'Founder, Veridian Home', initial: 'R', bg: 'linear-gradient(135deg,#2d3a72,#243064)' },
  { quote: 'COD confirmation calls cut our RTO by a third. Fewer fake orders, fewer returns, happier ops team.',
    name: 'Aisha Khan', role: 'Ops Lead, Northbound Beauty', initial: 'A', bg: 'linear-gradient(135deg,#e06408,#f57b1d)' },
  { quote: 'Our support inbox went quiet. The AI answers "where\'s my order?" before anyone has to. Setup took 20 minutes.',
    name: 'Daniel Brooks', role: 'COO, Atlas Apparel', initial: 'D', bg: 'linear-gradient(135deg,#0f9d58,#20c473)' },
];

export const STEPS = [
  { n: 1, title: 'Connect your store',     desc: 'Paste your Shopify URL. CallXcel reads your orders, products and customers in seconds, no code.' },
  { n: 2, title: 'Pick your scenarios',    desc: 'Turn on ready-made flows like cart recovery and COD confirmation. Smart defaults are pre-filled.' },
  { n: 3, title: 'Set it once, globally',  desc: 'Configure voice, hours and opt-out once. Every scenario inherits it, so a change applies everywhere instantly.' },
  { n: 4, title: 'Go live & watch revenue', desc: 'AI agents call and answer 24/7. Track recovered revenue, transcripts and outcomes in real time.' },
];
